import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLogin } from '../../context/LoginContext';
import { login, clientLogin } from "../../services/authApi";
import logger from '../../utils/logger';
import { getCookie } from '../../utils/cookies';

import userIcon from '../../assets/user_hollow.svg';
import googleIcon from '../../assets/google_svg.svg';
import appleIcon from '../../assets/apple_svg.svg';
import microsoftIcon from '../../assets/microsoft_svg.svg';

export default function LoginPage({ defaultMode = 'client' }) {
  const [isPro, setIsPro] = useState(defaultMode === 'pro');
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  
  const { user, error, success, setError, setSuccess, fetchUser, logout } = useLogin();
  const navigate = useNavigate();

  useEffect(() => {
    setUsername("");
    setPassword("");
    setError("");
    setSuccess("");
  }, [isPro, setError, setSuccess]);

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!username || !password) {
      setError("Veuillez remplir tous les champs.");
      return;
    }

    try {
      const { data } = await clientLogin({ username, password });
      if (data) {
        localStorage.setItem("csrf_access_token", decodeURIComponent(getCookie("csrf_access_token") || ""));
        localStorage.setItem("csrf_refresh_token", decodeURIComponent(getCookie("csrf_refresh_token") || ""));
        setSuccess("Connexion client réussie");
        
        await fetchUser();
        navigate("/customer", { replace: true });
      } else {
        setError("Identifiants invalides.");
      }
    } catch (err) {
      logger.error(err);
      setError("Erreur réseau, réessayez.");
    }
  };

  const handleProSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!username || !password) {
      setError("Veuillez remplir tous les champs.");
      return;
    }

    try {
      const res = await login({ username, password });
      if (res.status === 200) {
        const refreshToken = getCookie("csrf_refresh_token");
        if (refreshToken) {
          localStorage.setItem("csrf_refresh_token", decodeURIComponent(refreshToken));
        }

        const accessToken = getCookie("csrf_access_token");
        if (accessToken) {
          localStorage.setItem("csrf_access_token", decodeURIComponent(accessToken));
        }

        await fetchUser();
        setSuccess("Connexion pro réussie ✅");
        
        navigate("/cart", { replace: true });
      }
    } catch (err) {
      logger.error("Erreur lors de la connexion :", err);
      const errorMsg = err?.response?.data?.error || "Identifiants invalides ou erreur réseau.";
      setError(errorMsg);
    }
  };

  if (user?.authenticated) {
    return (
      <div className="col-span-2 flex justify-center items-center">
        <div className="card flex-col items-center gap-md" style={{ width: '100%'}}>
          <div className="text_with_icon m-md no-margin">
            <img src={userIcon} className="white_svg" alt="User" />
            <span className="font-size-title bold">Session active</span>
          </div>
          <span className="font-size-body muted_text text-center">
            Vous êtes déjà connecté à votre compte.
          </span>
          <button
            className="button-action w-full"
            onClick={() => navigate(user?.role === "client" ? "/customer" : "/cart")}
          >
            {user?.role === "client" ? "Accéder à mon espace" : "Accéder à la caisse"}
          </button>

          <button 
            className="button-action w-full" 
            onClick={logout}
          >
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="col-span-2" style={{ perspective: '1000px' }}>
      <div 
        style={{
          position: 'relative',
          width: '100%',
          transition: 'transform 0.6s',
          transformStyle: 'preserve-3d',
          transform: isPro ? 'rotateY(180deg)' : 'rotateY(0deg)'
        }}
      >
        <div 
          className="card flex-col gap-sm"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="text_with_icon m-md no-margin">
            <img src={userIcon} className="white_svg" alt="User" />
            <span className="font-size-label bold">Connexion</span>
            <div className="flex items-center gap-sm">
              <span className="font-size-body">Client</span>
              <label className="switch">
                <input type="checkbox" checked={isPro} onChange={() => setIsPro(!isPro)} />
                <span className="slider-round"></span>
              </label>
              <span className="font-size-body">Pro</span>
            </div>
          </div>
          
          <form className="flex gap-md" onSubmit={handleClientSubmit}>
            <div className="flex-col gap-sm flex-1">
              {error && !isPro && <div style={{ color: '#ff3561', fontSize: '14px' }}>{error}</div>}
              {success && !isPro && <div style={{ color: '#4ade80', fontSize: '14px' }}>{success}</div>}
              
              <input 
                type="text" 
                className="dark-input" 
                placeholder="Nom d'utilisateur"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <input 
                type="password" 
                className="dark-input" 
                placeholder="Mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              
              <div className="flex items-center gap-sm">
                <button type="submit" className="button-action">Se connecter</button>
                <button type="button" className="button-secondary" onClick={() => window.location.href = "/auth/login/google"}>
                  <img src={googleIcon} className="white_svg" width="16" alt="Google" />
                </button>
                <button type="button" className="button-secondary" onClick={() => window.location.href = "/auth/login/microsoft"}>
                  <img src={microsoftIcon} className="white_svg" width="16" alt="Microsoft" />
                </button>
                <button type="button" className="button-secondary">
                  <img src={appleIcon} className="white_svg" width="16" alt="Apple" />
                </button>
                <div className="flex-col justify-between items-center" style={{ minWidth: '150px' }}>
                  <button type="button" className="button-secondary w-full" onClick={() => navigate("/clients/new")}>
                    S'inscrire
                  </button>
                </div>
                <span 
                  className="muted_text font-size-body cursor-pointer" 
                  onClick={() => navigate("/forgot-password/client")}
                >
                  Mot de passe?
                </span>
              </div>
            </div>
          </form>
        </div>

        <div 
          className="card flex-col gap-sm"
          style={{ 
            backfaceVisibility: 'hidden', 
            transform: 'rotateY(180deg)', 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%' 
          }}
        >
          <div className="text_with_icon m-md no-margin">
            <img src={userIcon} className="white_svg" alt="User" />
            <span className="font-size-label bold">Connexion Pro</span>
            <div className="flex items-center gap-sm">
              <span className="font-size-body">Client</span>
              <label className="switch">
                <input type="checkbox" checked={isPro} onChange={() => setIsPro(!isPro)} />
                <span className="slider-round"></span>
              </label>
              <span className="font-size-body">Pro</span>
            </div>
          </div>
          
          <form className="flex gap-md h-full" onSubmit={handleProSubmit}>
            <div className="flex-col gap-sm flex-1">
              {error && isPro && <div style={{ color: '#ff3561', fontSize: '14px' }}>{error}</div>}
              {success && isPro && <div style={{ color: '#4ade80', fontSize: '14px' }}>{success}</div>}
              
              <input 
                type="text" 
                className="dark-input" 
                placeholder="Nom d'utilisateur"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <input 
                type="password" 
                className="dark-input" 
                placeholder="Mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              
              <div className="flex items-center gap-sm">
                <button type="submit" className="button-action">Se connecter</button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}